import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Database, Play, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MigrateWhatsApp = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleMigrate = async () => {
    if (!window.confirm('This will copy contact numbers to WhatsApp numbers for all members and advertisers who don\'t have a WhatsApp number set. Continue?')) {
      return;
    }

    setLoading(true);
    setResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/migrate-whatsapp-numbers`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult(response.data);
      toast.success(`Migration complete! Updated ${response.data.members_updated} members and ${response.data.advertisers_updated} advertisers`);
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(error.response?.data?.detail || 'Migration failed. Make sure you are logged in as admin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex items-center space-x-4 mb-8">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-3xl font-bold text-purple-700" data-testid="page-title">
              <Database className="inline mr-3" size={32} />
              Migrate WhatsApp Numbers
            </h1>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
            <div className="flex items-start">
              <AlertTriangle className="text-blue-600 mr-3 mt-1 flex-shrink-0" size={20} />
              <div className="text-sm text-blue-700">
                <p className="font-semibold mb-2">What does this do?</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Copies the <strong>Contact Number</strong> to the <strong>WhatsApp Number</strong> field</li>
                  <li>Only updates records where WhatsApp Number is currently empty</li>
                  <li>Applies to both <strong>Members</strong> and <strong>Advertisers</strong></li>
                  <li>Extracts the first valid 10-digit phone number from contact field</li>
                  <li>This is a one-time migration - safe to run multiple times</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-purple-700 mb-4">Run Migration</h2>
            
            <p className="text-gray-600 mb-6">
              Click the button below to populate the WhatsApp Number field for all members and advertisers 
              using their existing contact numbers.
            </p>

            <Button 
              onClick={handleMigrate} 
              disabled={loading}
              className="w-full py-6 text-lg bg-purple-600 hover:bg-purple-700"
              data-testid="migrate-button"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Migrating...
                </>
              ) : (
                <>
                  <Play className="mr-2" size={20} />
                  Start Migration
                </>
              )}
            </Button>
          </div>

          {/* Result */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6">
              <div className="flex items-center mb-4">
                <CheckCircle className="text-green-600 mr-3" size={28} />
                <h3 className="text-xl font-bold text-green-700">Migration Complete!</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-purple-600">{result.members_updated}</p>
                  <p className="text-sm text-gray-600">Members Updated</p>
                </div>
                <div className="bg-white p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-orange-600">{result.advertisers_updated}</p>
                  <p className="text-sm text-gray-600">Advertisers Updated</p>
                </div>
              </div>

              <p className="text-sm text-green-700 mt-4 text-center">
                WhatsApp numbers have been populated. You can now see them in Search results and Reports.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MigrateWhatsApp;
